<script lang="ts">
import { Vue, Component, Prop } from 'vue-facing-decorator';
import { useRoute, useRouter } from 'vue-router';
import { usePostHog } from '@/composables/usePostHog';

@Component
export default class LayerNavigator extends Vue {
  @Prop({ default: false }) private isPreview!: boolean;
  private route = useRoute();
  private router = useRouter();
  private scrollProgress = 0;
  private isMobileMenuOpen = false;
  private posthog = usePostHog().posthog;

  private layers = [
    { id: 'l0', label: 'L0', full: 'Physical & SDN', route: 'infra-l0' },
    { id: 'l1', label: 'L1', full: 'Orchestration', route: 'infra-l1' },
    { id: 'l2', label: 'L2', full: 'Data Services', route: 'infra-l2' },
    { id: 'l3', label: 'L3', full: 'SecOps & Obs', route: 'infra-l3' }
  ];

  mounted() {
    window.addEventListener('scroll', this.updateScrollProgress);
    this.updateScrollProgress(); // Initial check
    
    // Close mobile menu if clicked outside
    document.addEventListener('click', this.handleClickOutside);
  }

  beforeUnmount() {
    window.removeEventListener('scroll', this.updateScrollProgress);
    document.removeEventListener('click', this.handleClickOutside);
  }

  private updateScrollProgress() {
    const winScroll = document.documentElement.scrollTop;
    const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    if (height === 0) {
      this.scrollProgress = 100;
    } else {
      this.scrollProgress = (winScroll / height) * 100;
    }
  }

  private navigateTo(routeName: string) {
    this.posthog.capture('layer_nav_clicked', { target_route: routeName });
    this.router.push({ name: routeName });
    this.isMobileMenuOpen = false;
  }

  private isActive(routeName: string) {
    return this.route.name === routeName;
  }
  
  private toggleMobileMenu(event: Event) {
    event.stopPropagation();
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }
  
  private handleClickOutside(event: Event) {
    if (this.isMobileMenuOpen && window.innerWidth <= 768) {
      const nav = this.$el as HTMLElement;
      if (nav && !nav.contains(event.target as Node)) {
        this.isMobileMenuOpen = false;
      }
    }
  }
  
  // Computed property to get the currently active item
  get activeItem() {
    if (this.isActive('infrastructure')) {
      return { type: 'overview', icon: 'mdi-view-dashboard-outline' };
    }
    const activeLayer = this.layers.find(l => this.isActive(l.route));
    if (activeLayer) {
      return { type: 'layer', label: activeLayer.label };
    }
    return null;
  }
}
</script>

<template>
  <nav v-if="!isPreview" class="layer-navigator" :class="{ 'mobile-open': isMobileMenuOpen }">
    
    <!-- Mobile Active Toggle Button (Visible only on mobile when closed) -->
    <div class="mobile-toggle ln-glass-elevated" @click="toggleMobileMenu" v-if="activeItem">
      <div class="layer-item active">
        <span v-if="activeItem.type === 'overview'" class="mdi" :class="activeItem.icon"></span>
        <span v-else class="layer-label">{{ activeItem.label }}</span>
        
        <div class="scroll-ring">
          <svg viewBox="0 0 36 36">
            <path class="ring-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            <path class="ring-progress" 
                  :style="{ strokeDasharray: scrollProgress + ', 100' }"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
          </svg>
        </div>
      </div>
    </div>

    <!-- The actual navigation pill (Always visible on desktop, hidden/shown on mobile) -->
    <div class="nav-pill ln-glass-elevated">
      <!-- Overview at the TOP -->
      <div class="layer-item overview-btn" 
           :class="{ 'active': isActive('infrastructure') }"
           @click="navigateTo('infrastructure')" 
           v-tooltip.left="'Overview'">
        <span class="mdi mdi-view-dashboard-outline"></span>
        
        <!-- Progress ring for Overview if active -->
        <div v-if="isActive('infrastructure')" class="scroll-ring">
          <svg viewBox="0 0 36 36">
            <path class="ring-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            <path class="ring-progress" 
                  :style="{ strokeDasharray: scrollProgress + ', 100' }"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
          </svg>
        </div>
      </div>

      <div class="separator"></div>

      <!-- Layers L0 - L3 -->
      <div v-for="layer in layers" :key="layer.id" 
           class="layer-item" 
           :class="{ 'active': isActive(layer.route) }"
           @click="navigateTo(layer.route)"
           v-tooltip.left="layer.full">
        
        <span class="layer-label">{{ layer.label }}</span>
        
        <!-- Individual progress ring for active layer -->
        <div v-if="isActive(layer.route)" class="scroll-ring">
          <svg viewBox="0 0 36 36">
            <path class="ring-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
            <path class="ring-progress" 
                  :style="{ strokeDasharray: scrollProgress + ', 100' }"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
          </svg>
        </div>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.layer-navigator {
  position: fixed;
  right: 2rem;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mobile-toggle {
  display: none; /* Hidden by default on desktop */
}

.nav-pill {
  padding: 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  border-radius: 3rem;
  border: 1px solid var(--ln-glass-border-strong);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
}

.layer-item {
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  position: relative;
  transition: all var(--ln-transition-normal) var(--ln-ease-bounce);
  background: var(--ln-glass-surface);
  color: var(--ln-text-muted);
  font-weight: 700;
  font-size: 0.8rem;
}

.layer-item:hover {
  transform: scale(1.15);
  color: var(--ln-teal-deep);
  background: var(--ln-glass-elevated);
}

.layer-item.active {
  color: var(--ln-teal-deep);
  background: var(--ln-glass-elevated);
  transform: scale(1.1);
  box-shadow: 0 0 15px rgba(44, 122, 123, 0.2);
}

.layer-label {
  z-index: 2;
}

.scroll-ring {
  position: absolute;
  inset: -4px;
  z-index: 1;
}

.scroll-ring svg {
  width: 100%;
  height: 100%;
}

.ring-bg {
  fill: none;
  stroke: var(--ln-glass-border);
  stroke-width: 2;
}

.ring-progress {
  fill: none;
  stroke: var(--ln-teal-deep);
  stroke-width: 2.5;
  stroke-linecap: round;
  transition: stroke-dasharray 0.1s linear;
}

.overview-btn {
  font-size: 1.2rem;
}

.separator {
  height: 1px;
  background: var(--ln-glass-border);
  margin: 0 0.5rem;
}

@media (max-width: 768px) {
  .layer-navigator {
    top: 2rem; /* Move to top */
    right: 1.5rem;
    transform: none; /* Remove centering transform */
    align-items: flex-end; /* Align to the right */
  }
  
  .mobile-toggle {
    display: flex;
    padding: 0.5rem;
    border-radius: 50%;
    border: 1px solid var(--ln-glass-border-strong);
    cursor: pointer;
    box-shadow: 0 4px 12px var(--ln-glass-shadow);
    z-index: 1002;
  }
  
  .mobile-toggle .layer-item {
    transform: none !important; /* Prevent double scaling on the toggle button */
    box-shadow: none !important;
  }

  /* Hide the main menu by default on mobile */
  .nav-pill {
    position: absolute;
    top: 0;
    right: 0;
    opacity: 0;
    pointer-events: none;
    transform: translateY(-20px) scale(0.9);
    transform-origin: top right;
    box-shadow: 0 10px 30px var(--ln-glass-shadow-elevated);
  }

  /* Show when open */
  .layer-navigator.mobile-open .nav-pill {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0) scale(1);
  }
  
  /* Hide the toggle button itself when menu is open so it looks like it expanded */
  .layer-navigator.mobile-open .mobile-toggle {
    opacity: 0;
    pointer-events: none;
  }
}
</style>
